<template>
  <b-modal
    v-model="show"
    title="Selected Tradies Information"
    modal-class="share-tradies-modal"
    centered
    size="lg"
    :hide-footer="true"
    @show="onShow"
    @hidden="onHidden"
  >
    <b-overlay
      :show="isBulkSharing || isFileSizeLoading || isUsersLoading"
      rounded="sm"
      opacity="0.65"
    >
      <template #overlay>
        <div class="text-center px-3">
          <digger-loader />
          <p v-if="shareModalBusyMessage" class="small mt-2 mb-0 text-dark">
            {{ shareModalBusyMessage }}
          </p>
        </div>
      </template>
      <div class="progress-container">
        <div
          class="progress-bar"
          :class="`progress-bar--${progressVariant}`"
          :style="{ width: `${progressValue}%` }"
        ></div>
      </div>
      <p
        :class="{
          'text-danger': totalSelectedMb > MAX_MB,
        }"
      >
        {{ progressLabel }}
      </p>

      <b-form-input
        v-model="search"
        placeholder="Search tradies by name or email..."
        class="mb-3 tradie-share-search"
      />

      <b-form @submit.prevent="onSubmitTradiesData">
        <div
          v-if="!filteredUsers.length"
          class="text-center text-muted small py-3"
        >
          No tradies match "{{ search }}".
        </div>

        <div
          v-for="user in pagedUsers"
          :key="user.Id"
          class="tradie-share-user-block"
          style="max-height: 70vh; overflow-y: scroll"
        >
          <!-- USER ROWS -->
          <div
            class="d-flex align-items-stretch w-100 tradie-share-user-header"
            :id="`parent-${user.Id}`"
          >
            <div
              class="d-flex align-items-center justify-content-center flex-shrink-0 px-2 tradie-share-checkbox-gutter"
            >
              <b-form-checkbox
                class="mb-0"
                v-model="user._allFilesChecked"
                @change="parentToggle($event, 'user', user.Id)"
                >&nbsp;
              </b-form-checkbox>
            </div>
            <b-button
              squared
              v-b-toggle="`collapse-${user.Id}`"
              block
              :pressed="false"
              variant="primary"
              class="tradie-share-user-toggle-btn"
              style="
                height: 100%;
                border: none;
                padding-left: 16px;
                padding-right: 16px;
              "
              @click="toggleChevron($event)"
            >
              <div
                class="d-flex justify-content-between align-items-center w-100"
              >
                <div>
                  <span>{{
                    user['Full Name'] || user.email || 'Unknown User'
                  }}</span>
                </div>

                <b-icon icon="chevron-down" />
              </div>
            </b-button>
          </div>

          <!-- SUBROWS -->
          <b-collapse :id="`collapse-${user.Id}`">
            <!-- FILES  -->
            <template v-if="user.Files.length > 0">
              <div v-for="file in user.Files" :key="file.id">
                <div
                  class="d-flex align-items-stretch w-100 bg-light-darker tradie-share-file-row"
                >
                  <div
                    class="d-flex align-items-center justify-content-center flex-shrink-0 px-2 tradie-share-checkbox-gutter"
                  >
                    <b-form-checkbox
                      class="mb-0"
                      v-model="file.selected"
                      @click.stop
                      @change="excluded($event, file.id, 'files', user)"
                      >&nbsp;</b-form-checkbox
                    >
                  </div>
                  <b-button
                    squared
                    variant="light-darker"
                    class="flex-fill text-left no-outline d-flex align-items-center tradie-share-file-btn"
                    size="sm"
                    block
                    style="border: none; min-height: 2.5rem"
                  >
                    <div
                      class="d-flex align-items-center w-100 px-2 py-1 tradie-share-file-grid"
                    >
                      <span
                        class="text-truncate text-left pr-2 tradie-share-file-name"
                      >
                        {{ file.name }}
                      </span>
                      <div class="tradie-share-file-meta">
                        <span
                          class="text-truncate text-center px-2 tradie-share-file-status"
                        >
                          {{ file.status }}
                        </span>
                        <span
                          class="text-nowrap text-right pl-2 tradie-share-file-size"
                        >
                          <template v-if="fileSizeById[file.id] !== undefined">
                            {{ Number(fileSizeById[file.id] || 0).toFixed(2) }}
                            MB
                          </template>
                          <template v-else>&mdash;</template>
                        </span>
                      </div>
                    </div>
                  </b-button>
                </div>
              </div>
            </template>
            <!-- PERSONAL INFO  -->
            <div
              class="d-flex align-items-stretch w-100 bg-light-darker tradie-share-personal-row"
            >
              <div
                class="d-flex align-items-center justify-content-center flex-shrink-0 px-2 tradie-share-checkbox-gutter"
              >
                <b-form-checkbox
                  class="mb-0"
                  v-model="user._allPiChecked"
                  @click.stop
                  @change="parentToggle($event, 'personal_info', user.Id)"
                  >&nbsp;
                </b-form-checkbox>
              </div>
              <b-button
                squared
                v-b-toggle="`collapsePersonal-${user.Id}`"
                size="sm"
                block
                variant="light-darker"
                class="flex-fill text-left no-outline"
                style="border: transparent; min-height: 2.5rem"
                @click="toggleChevron($event)"
              >
                <div
                  class="d-flex justify-content-between align-items-center w-100 px-2"
                >
                  <span>Personal Information</span>
                  <b-icon icon="chevron-down" />
                </div>
              </b-button>
            </div>

            <b-collapse :id="`collapsePersonal-${user.Id}`">
              <!-- PERSONAL INFO SUBROWS -->
              <div v-for="[key, value] in Object.entries(user)" :key="key">
                <div
                  v-if="
                    !key.startsWith('_') &&
                    key !== 'Files' &&
                    key !== 'excluded' &&
                    key !== 'Id' &&
                    key !== 'Avatar Permanent Url' &&
                    key !== 'Avatar Permanent Thumb Url' &&
                    key !== 'Profile Photo' &&
                    key !== 'Signup Consent Given' &&
                    key !== 'Ai Consent Given' &&
                    key !== 'Manual Upload Consent Given' &&
                    key !== 'Privacy Consent Given' &&
                    key !== 'Signup Consent Given At' &&
                    key !== 'Ai Consent Given At' &&
                    key !== 'Manual Upload Consent Given At' &&
                    key !== 'Requires Password Change' &&
                    key !== 'Country Code'
                  "
                >
                  <div
                    class="d-flex align-items-stretch w-100 bg-light tradie-share-personal-subrow"
                  >
                    <div
                      class="d-flex align-items-center justify-content-center flex-shrink-0 px-2 tradie-share-checkbox-gutter"
                    >
                      <b-form-checkbox
                        class="mb-0"
                        :checked="!user.excluded.personal_info.includes(key)"
                        @click.stop
                        @change="excluded($event, key, 'personal_info', user)"
                        >&nbsp;
                      </b-form-checkbox>
                    </div>
                    <b-button
                      squared
                      size="sm"
                      block
                      variant="light"
                      class="flex-fill text-left no-outline"
                      style="border: transparent; min-height: 2.5rem"
                    >
                      <div
                        class="d-flex align-items-center w-100 px-2 py-1 tradie-share-personal-fields"
                      >
                        <span
                          class="text-truncate text-left pr-3 tradie-share-personal-key"
                          >{{ key }}</span
                        >
                        <span
                          class="text-truncate text-left tradie-share-personal-val"
                          >{{ formatValue(key, value) }}</span
                        >
                      </div>
                    </b-button>
                  </div>
                </div>
              </div>
            </b-collapse>
          </b-collapse>
        </div>

        <b-pagination
          v-if="filteredUsers.length > perPage"
          v-model="currentPage"
          :total-rows="filteredUsers.length"
          :per-page="perPage"
          align="center"
          class="mt-3 mb-0"
        />
        <p
          v-if="filteredUsers.length"
          align="center"
          class="small text-muted mt-2 mb-0"
        >
          Showing {{ pagedUsers.length }} out of {{ filteredUsers.length }}
          tradies
        </p>

        <b-row class="mb-3 mt-3">
          <b-col cols="9" class="tradie-share-email-col">
            <b-form-input
              v-model="recipientEmail"
              placeholder="Recipient Email"
              style="height: 100%"
            ></b-form-input>
          </b-col>
          <b-col
            cols="3"
            class="d-flex justify-content-end tradie-share-submit-col"
          >
            <b-button
              type="submit"
              variant="primary"
              block
              :disabled="
                isBulkSharing ||
                isFileSizeLoading ||
                totalSelectedMb > MAX_MB ||
                !isRecipientEmailValid
              "
              >Submit</b-button
            >
          </b-col>
        </b-row>
      </b-form>
    </b-overlay>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  TRADIEFILESHARE_EXPORT_TIMEOUT_MS,
  TRADIEFILESHARE_SIZE_TIMEOUT_MS,
  uniqueTradieShareEntries,
} from '@/utils/tradiefileshareExport'

export default {
  name: 'ShareTradiesModal',

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    selectedTradies: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      isUsersLoading: false,
      isBulkSharing: false,
      isFileSizeLoading: false,
      recipientEmail: '',
      fileSizeSummary: null,
      fileSizeById: {},
      MAX_MB: 300,
      currentPage: 1,
      perPage: 10,
      search: '',
    }
  },

  computed: {
    ...mapGetters('user', ['detailedUsers']),

    filteredUsers() {
      const term = this.search.trim().toLowerCase()
      if (!term) return this.detailedUsers
      return this.detailedUsers.filter((user) => {
        const name = (user['Full Name'] || '').toLowerCase()
        const email = (user['Email'] || '').toLowerCase()
        return name.includes(term) || email.includes(term)
      })
    },

    pagedUsers() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredUsers.slice(start, start + this.perPage)
    },

    show: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },

    totalSelectedMb() {
      if (!this.fileSizeSummary || !this.fileSizeById) return 0
      const excludedIds = new Set(
        (this.detailedUsers || []).flatMap((user) => {
          const excluded = user && user.excluded
          return excluded && excluded.files ? excluded.files : []
        }),
      )
      return Object.entries(this.fileSizeById)
        .filter(([id]) => !excludedIds.has(Number(id)))
        .reduce((sum, [, mb]) => sum + mb, 0)
    },

    progressValue() {
      if (!this.totalSelectedMb) return 0
      const clamped = Math.min(this.totalSelectedMb, this.MAX_MB)
      return (clamped / this.MAX_MB) * 100
    },

    progressVariant() {
      return this.totalSelectedMb > this.MAX_MB ? 'danger' : 'primary'
    },

    progressLabel() {
      return `${this.totalSelectedMb.toFixed(2)} / ${this.MAX_MB} MB`
    },

    isRecipientEmailValid() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.recipientEmail.trim())
    },

    shareModalBusyMessage() {
      if (this.isBulkSharing) {
        return 'Preparing export… This can take several minutes for large selections.'
      }
      if (this.isUsersLoading) return 'Loading tradies…'
      if (this.isFileSizeLoading) return 'Calculating file sizes…'
      return ''
    },
  },

  watch: {
    search() {
      this.currentPage = 1
    },
  },

  methods: {
    ...mapActions('user', {
      getUsers: 'getUsers',
    }),

    onShow() {
      // Fires before the open transition starts, so we can:
      //   1. Paint the loading overlay together with the modal (no empty flash).
      //   2. Kick off both API calls in parallel — they don't depend on each
      //      other, so overlapping them halves perceived wait time.
      this.isUsersLoading = true
      this.isFileSizeLoading = true
      this.isBulkSharing = false
      this.fileSizeSummary = null
      this.fileSizeById = {}
      this.recipientEmail = ''
      this.currentPage = 1
      this.search = ''

      this.fetchSelectedTradiesFullData().finally(() => {
        this.isUsersLoading = false
      })

      this.getFileSize()
        .catch(() => {
          this.showToast(
            'Error',
            'Could not load file sizes for export',
            'danger',
          )
          this.fileSizeSummary = null
          this.fileSizeById = {}
        })
        .finally(() => {
          this.isFileSizeLoading = false
        })
    },

    onHidden() {
      this.isUsersLoading = false
      this.isFileSizeLoading = false
      this.isBulkSharing = false
      this.fileSizeSummary = null
      this.fileSizeById = {}
      this.recipientEmail = ''
      this.currentPage = 1
      this.search = ''
    },

    async fetchSelectedTradiesFullData() {
      const selectedTradiesString = (this.selectedTradies || []).join('|')
      // Ask the backend for every selected tradie in a single page. Without
      // this, the store action falls back to limit=10 and silently truncates
      // the modal's data set when more than 10 tradies are selected (and adds
      // pagination latency to the share flow).
      await this.getUsers({
        userIds: selectedTradiesString,
        limit: Math.max(this.selectedTradies.length, 10),
      })
        .then(() =>
          this.detailedUsers.forEach((user) => {
            // Initialise reactive state directly. The previous version
            // simulated ~25 DOM clicks per user via $nextTick, which froze the
            // main thread for several seconds on large selections. Setting
            // state through Vue's reactivity does the same thing in O(files)
            // assignments with no synthetic events.
            this.$set(user, 'excluded', {
              id: user.Id,
              files: [],
              personal_info: [],
            })
            this.$set(user, '_allFilesChecked', true)
            this.$set(user, '_allPiChecked', true)
            if (Array.isArray(user.Files)) {
              user.Files.forEach((file) => {
                this.$set(file, 'selected', true)
              })
            }
          }),
        )
        .catch((err) => console.log(err))
    },

    async getFileSize() {
      // Build the payload directly from the selected IDs so this call can run
      // in parallel with getUsers. At this point every entry's excluded arrays
      // are empty by definition (the user hasn't unchecked anything yet), so
      // this is equivalent to deriving from detailedUsers.
      const tradiesPayload = uniqueTradieShareEntries(
        (this.selectedTradies || []).map((id) => ({
          id,
          files: [],
          personal_info: [],
        })),
      )
      const payload = { tradies: JSON.stringify(tradiesPayload) }
      const { data } = await this.$api.post(
        '/tradiefileshare/size',
        JSON.parse(JSON.stringify(payload)),
        { timeout: TRADIEFILESHARE_SIZE_TIMEOUT_MS },
      )
      this.fileSizeSummary = data

      this.fileSizeById = Object.fromEntries(
        data.tradies.flatMap((tradie) =>
          tradie.files.map((file) => [file.id, file.megabytes]),
        ),
      )
    },

    async onSubmitTradiesData() {
      if (this.isBulkSharing) return
      if (!this.isRecipientEmailValid) {
        this.showToast(
          'Warning',
          'Please enter a valid recipient email',
          'warning',
        )
        return
      }
      if (this.totalSelectedMb > this.MAX_MB) {
        this.showToast(
          'Warning',
          'Please deselect files until total is under 30 MB',
          'warning',
        )
        return
      }
      this.isBulkSharing = true
      const data = uniqueTradieShareEntries(
        this.detailedUsers.map((user) => user.excluded),
      )

      const payload = new FormData()
      payload.append('tradies', JSON.stringify(data))
      payload.append('email', this.recipientEmail)

      try {
        this.showToast('Info', 'Preparing export…', 'info')
        await this.$api.post('/tradiefileshare', payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: TRADIEFILESHARE_EXPORT_TIMEOUT_MS,
        })
        this.showToast('Success', 'Tradies shared successfully!')
        this.show = false
        this.fileSizeSummary = null
        this.fileSizeById = {}
        this.$emit('shared')
      } catch (err) {
        this.showToast('Error', 'Failed to share tradies', 'danger')
      } finally {
        this.isBulkSharing = false
      }
    },

    excluded(event, key, option, user) {
      if (event === true) {
        const index = user.excluded[option].indexOf(key)
        if (index > -1) {
          user.excluded[option].splice(index, 1)
        }
      } else if (event === false) {
        user.excluded[option].push(key)
      } else {
        this.showToast('Error', 'event neither true or false', 'danger')
      }
    },

    parentToggle(event, option, id) {
      if (option === 'user') {
        let subrows = document.getElementById(`collapse-${id}`)
        if (subrows) {
          const checkboxes = subrows.querySelectorAll('input[type="checkbox"]')
          checkboxes.forEach((checkbox) => {
            if (checkbox.checked !== event) {
              checkbox.click()
            }
          })
        }
      } else if (option === 'personal_info') {
        let subrows = document.getElementById(`collapsePersonal-${id}`)
        if (subrows) {
          const checkboxes = subrows.querySelectorAll('input[type="checkbox"]')
          checkboxes.forEach((checkbox) => {
            if (checkbox.checked !== event) {
              checkbox.click()
            }
          })
        }
      } else {
        this.showToast('Error', 'event neither true or false', 'danger')
      }
    },

    toggleChevron(event) {
      // Find the b-icon within the clicked button
      const button = event.currentTarget
      const icon = button.querySelector('.b-icon')

      if (icon) {
        const currentRotation = icon.style.transform || 'rotate(0deg)'
        const isRotated = currentRotation.includes('180deg')

        if (isRotated) {
          icon.style.transform = 'rotate(0deg)'
        } else {
          icon.style.transform = 'rotate(180deg)'
        }
      }
    },

    formatValue(key, value) {
      if (value === null) {
        switch (key) {
          case 'Gender':
            return 'Prefer Not To Say'
          case 'Description':
            return 'Empty'
          case 'Profile Photo':
            return ''
          default:
            return '-'
        }
      } else if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No'
      }
      if (key === 'Birthdate') {
        return this.formatBirthdate(value)
      }
      return value
    },

    formatBirthdate(value) {
      if (!value) return value
      const str = String(value)
      // YYYY-MM-DD (or ISO with time) → DD/MM/YYYY
      const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`
      // MM/DD/YYYY → DD/MM/YYYY (swap first two segments)
      const slash = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/)
      if (slash) return `${slash[2]}/${slash[1]}/${slash[3]}`
      return value
    },

    showToast(title, message, variant = 'success') {
      this.$bvToast.toast(message, {
        title: title,
        autoHideDelay: 3000,
        variant: variant,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.b-icon {
  transition: transform 0.5s ease;
}

.bg-light-darker {
  background-color: #e4e4e4 !important;
}

.btn-light-darker {
  background-color: #e4e4e4 !important;
  border-color: #e4e4e4 !important;
  color: #495057 !important;
}

.btn-light-darker:hover {
  background-color: #d2d5d5 !important;
  border-color: #d2d5d5 !important;
  color: #495057 !important;
}

.btn-light-darker:focus {
  box-shadow: 0 0 0 0.2rem rgba(240, 240, 240, 0.5) !important;
}

.no-outline:focus {
  box-shadow: none !important;
}

.progress-container {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: #e9eef5;
}

.progress-bar {
  height: 100%;
  border-radius: 4px;
  background: #006cff;
  transition: width 0.2s ease;
}

.progress-bar--primary {
  background: #006cff;
}

.progress-bar--danger {
  background: #e52c00;
}

/* Share tradies modal: gutter + stable columns (size slot always reserved) */
.tradie-share-checkbox-gutter {
  width: 2.75rem;
  flex-shrink: 0;
  box-sizing: border-box;
}

.tradie-share-user-header {
  min-height: 2.5rem;
  border-top: 1px solid grey;
  background-color: #006cff;

  // Centre the checkbox and tradie name on a shared line at all widths.
  // The row is `align-items-stretch` (a utility with !important), which
  // left the button's text and the checkbox box at different vertical
  // positions — so this override needs !important to win.
  align-items: center !important;
}

// Centre the header's toggle-button text (rather than letting it sit at the
// top of the full-height button).
.tradie-share-user-toggle-btn {
  display: flex;
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
  min-height: 2.5rem;
}

// The checkbox box/checkmark use a fixed `top: 0.25rem`; pin them to the
// vertical centre so the box lines up with the name beside it.
/deep/ .tradie-share-user-header .custom-control-label::before,
/deep/ .tradie-share-user-header .custom-control-label::after {
  top: 50%;
  transform: translateY(-50%);
}

.tradie-share-file-row,
.tradie-share-personal-row,
.tradie-share-personal-subrow {
  border-top: 1px solid #d9d9d9;
}

.tradie-share-file-grid .tradie-share-file-name {
  flex: 1 1 0;
  min-width: 0;
}

.tradie-share-file-grid .tradie-share-file-status {
  flex: 0 0 8.85rem;
  min-width: 0;
}

.tradie-share-file-grid .tradie-share-file-size {
  flex: 0 0 4.9rem;
  min-width: 4.9rem;
  box-sizing: border-box;
}

// Groups status + size together (see the media query below for why), but
// stays invisible in the layout tree at normal sizes so status/size keep
// participating directly in .tradie-share-file-grid's flex layout exactly
// as before — this changes nothing above 575px.
.tradie-share-file-meta {
  display: contents;
}

.tradie-share-personal-fields .tradie-share-personal-key {
  flex: 0 0 42%;
  min-width: 0;
}

.tradie-share-personal-fields .tradie-share-personal-val {
  flex: 1 1 0;
  min-width: 0;
}

// Small-phone-only tightening. Scoped inside this media query so nothing
// changes at 576px and up.
@media (max-width: 575px) {
  /deep/ .share-tradies-modal {
    .modal-dialog {
      margin: 0.75rem;
      max-width: calc(100% - 1.5rem);
    }

    .modal-content {
      overflow-x: hidden;
    }

    .modal-body {
      overflow-x: hidden;
    }

    .modal-title {
      font-size: 1.15rem;
    }

    .modal-body {
      padding: 0 0.75rem;
    }
  }

  // The file row's status (8.85rem) and size (4.9rem) columns are fixed
  // widths that left almost nothing for the file name on a 320px screen
  // (it was truncating to a single letter). Wrap the name onto its own
  // line, with status and size sharing a second line below it.
  //
  // Status/size used to be loose siblings of the name in the same wrap
  // container, so whether they shared a line depended on how long that
  // particular status text was ("expiring soon" fit, "pending approval"
  // often didn't) — an inconsistent, crowded result row to row. They're
  // now grouped in their own wrapper (.tradie-share-file-meta) so they
  // always appear together on one dedicated line, at a fixed layout,
  // regardless of text length.
  .tradie-share-file-grid {
    flex-wrap: wrap;
  }

  .tradie-share-file-grid .tradie-share-file-name {
    flex: 1 1 100% !important;
    text-align: left !important;
    padding-right: 0 !important;
    margin-bottom: 0.15rem;
    font-size: 0.85rem;
  }

  .tradie-share-file-meta {
    display: flex !important;
    flex: 1 1 100% !important;
    justify-content: space-between;
    align-items: baseline;
  }

  .tradie-share-file-meta .tradie-share-file-status {
    flex: 1 1 auto !important;
    min-width: 0 !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left !important;
    padding-left: 0 !important;
    font-size: 0.8rem;
    color: #6c757d;
  }

  .tradie-share-file-meta .tradie-share-file-size {
    flex: 0 0 auto !important;
    font-size: 0.8rem;
    color: #6c757d;
  }

  // Stack the personal-info field name over its value, mirroring the file
  // rows (name on top, muted detail below), instead of a side-by-side
  // key/value split that squeezed both on a 320px screen.
  .tradie-share-personal-fields {
    flex-wrap: wrap;
  }

  .tradie-share-personal-fields .tradie-share-personal-key {
    flex: 1 1 100% !important;
    min-width: 0;
    padding-right: 0 !important;
    margin-bottom: 0.1rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: #364052;
  }

  .tradie-share-personal-fields .tradie-share-personal-val {
    flex: 1 1 100% !important;
    min-width: 0;
    font-size: 0.8rem;
    color: #6c757d;
  }

  // "Submit" was cut down to "Subm" in the fixed 25%-width column;
  // stack the email and button on their own full-width rows instead.
  .tradie-share-email-col,
  .tradie-share-submit-col {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }

  .tradie-share-submit-col {
    margin-top: 0.5rem;
  }

  // Belt-and-suspenders: contain any leftover overflow (e.g. an
  // unbroken long name/status string) inside this scrollable block
  // instead of letting it push the whole modal wider and trigger a
  // page-level horizontal scrollbar.
  .tradie-share-user-block {
    overflow-x: hidden;
    max-width: 100%;
  }

  .tradie-share-user-header,
  .tradie-share-file-row,
  .tradie-share-personal-row,
  .tradie-share-personal-subrow {
    min-width: 0;
  }

  .tradie-share-user-header .btn {
    min-width: 0;
  }

  // The toggle buttons use `block` (width: 100%), which sets their flex
  // basis to 100% of the row instead of "remaining space after the
  // checkbox gutter" — that's what was pushing content past the right
  // edge (and, since it's clipped now, cutting text off instead of
  // scrolling to it). Constrain them to actually share the row.
  .tradie-share-user-toggle-btn,
  .tradie-share-file-btn {
    width: auto !important;
    min-width: 0 !important;
    flex: 1 1 auto !important;
  }

  // Pull the name/content in tight against the checkbox. The default
  // gutter (2.75rem) was centring the checkbox, so its empty &nbsp; label
  // left dead space to the right of the box, and the wide gutter + button
  // padding pushed the name so far right it ran off the edge. Keep a small
  // pad to the left of the box, left-align it, and remove the gap between
  // the gutter and the content so name/status/size shift left and fit.
  .tradie-share-checkbox-gutter {
    width: 1.75rem !important;
    padding-left: 0.5rem !important;
    padding-right: 0 !important;
    justify-content: flex-start !important;
  }

  // Strip the file/personal button's own horizontal padding so the name
  // sits flush against the checkbox instead of being pushed right by the
  // button's default `.btn` padding.
  .tradie-share-file-btn {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  // Centre the checkbox and the tradie name on a shared line. The row is
  // `align-items-stretch` (so children fill height), but the button and the
  // checkbox gutter ended up with different content heights, leaving the
  // name and box at different vertical positions. Centre the row's children
  // and strip the button's vertical padding so its text sits on the same
  // centre line as the box.
  .tradie-share-user-header {
    align-items: center !important;
  }

  .tradie-share-user-toggle-btn {
    padding-left: 0.35rem !important;
    padding-right: 0.5rem !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    height: auto !important;
    min-height: 2.5rem !important;
    display: flex !important;
    align-items: center !important;
  }

  // Nudge the file / personal-info checkboxes a touch to the right for a
  // cleaner indent under the header. (The header checkbox is left alone —
  // it's already aligned well.)
  .tradie-share-file-row .tradie-share-checkbox-gutter,
  .tradie-share-personal-row .tradie-share-checkbox-gutter,
  .tradie-share-personal-subrow .tradie-share-checkbox-gutter {
    padding-left: 0.7rem !important;
  }

  // Pull the size clear of the right edge (padding-right), while giving the
  // name/status a small left indent from the checkbox. The size stays put
  // — it's right-aligned to the grid's right edge, which padding-right
  // fixes independently of the left indent.
  .tradie-share-file-grid {
    padding-left: 0.4rem !important;
    padding-right: 0.65rem !important;
  }

  .tradie-share-personal-fields {
    padding-left: 0 !important;
    padding-right: 0.65rem !important;
  }

  // Checkboxes looked off-centre once file rows grew to two lines (name +
  // status/size). Make the wrapper a flex row so the box centres on the
  // row's height, and collapse the empty &nbsp; label so it stops adding
  // dead width to the right of the box.
  /deep/ .tradie-share-checkbox-gutter .custom-control {
    display: flex !important;
    align-items: center !important;
    min-height: 0 !important;
    padding-left: 1.25rem !important;
  }

  /deep/ .tradie-share-checkbox-gutter .custom-control-label {
    width: 0 !important;
    min-width: 0 !important;
    line-height: 1.25rem !important;
  }

  // The checkbox box/checkmark use a fixed `top: 0.25rem`, which no longer
  // centres once the label line-height is collapsed — pin them to the
  // vertical centre of the label instead so the box lines up with the
  // tradie name beside it.
  /deep/ .tradie-share-checkbox-gutter .custom-control-label::before,
  /deep/ .tradie-share-checkbox-gutter .custom-control-label::after {
    top: 50% !important;
    transform: translateY(-50%) !important;
  }
}
</style>
